import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase, BUCKET } from '../supabase';

const CATEGORIES = [
  { id: 'photos', label: 'Photos', accept: 'image/*', desc: 'Job sites, team, equipment, before/after, service areas' },
  { id: 'video', label: 'Video Footage', accept: 'video/*', desc: 'Raw footage — job sites, team intros, before/after, service walk-throughs' },
  { id: 'logos', label: 'Logo Files', accept: 'image/*,.svg,.pdf,.ai,.eps', desc: 'All logo formats and color variations' },
  { id: 'other', label: 'Other Files', accept: '*', desc: 'Brand guidelines, documents, miscellaneous assets' },
];

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function FileCard({ file, onDelete }) {
  const isImage = file.metadata?.mimetype?.startsWith('image/');
  const isVideo = file.metadata?.mimetype?.startsWith('video/');
  const [url, setUrl] = useState(null);

  useEffect(() => {
    const { data } = supabase.storage.from(BUCKET).getPublicUrl(file.name);
    setUrl(data?.publicUrl);
  }, [file.name]);

  return (
    <div style={{ background: '#1e1e1e', border: '1px solid #2a2a2a', borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
      {isImage && url ? (
        <img src={url} alt={file.name.split('/').pop()} style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }} />
      ) : isVideo ? (
        <div style={{ width: '100%', aspectRatio: '16/9', background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 11, color: '#555', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Video</div>
        </div>
      ) : (
        <div style={{ width: '100%', aspectRatio: '1', background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 11, color: '#555', textTransform: 'uppercase', letterSpacing: '0.08em' }}>File</div>
        </div>
      )}
      <button
        onClick={() => onDelete(file.name)}
        style={{ position: 'absolute', top: 5, right: 5, background: 'rgba(0,0,0,0.75)', border: '1px solid #444', borderRadius: 3, color: '#aaa', fontSize: 10, width: 20, height: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >x</button>
      {url && (
        <a href={url} target="_blank" rel="noreferrer" style={{ position: 'absolute', bottom: 5, right: 5, background: 'rgba(0,0,0,0.75)', border: '1px solid #444', borderRadius: 3, color: '#aaa', fontSize: 9, padding: '2px 6px', textDecoration: 'none', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.06em', textTransform: 'uppercase' }}>View</a>
      )}
      <div style={{ padding: '6px 8px 4px', fontSize: 11, color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file.name.split('/').pop()}</div>
      <div style={{ padding: '0 8px 8px', fontSize: 10, color: '#444' }}>{formatSize(file.metadata?.size)}</div>
    </div>
  );
}

function UploadSection({ category, needsNote }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(true);
  const inputRef = useRef();

  const loadFiles = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.storage.from(BUCKET).list(category.id, { limit: 200 });
    if (!error && data) setFiles(data.filter(f => f.name !== '.emptyFolderPlaceholder'));
    setLoading(false);
  }, [category.id]);

  useEffect(() => { loadFiles(); }, [loadFiles]);

  const handleUpload = async (fileList) => {
    setUploading(true);
    const uploads = Array.from(fileList);
    for (const file of uploads) {
      const path = `${category.id}/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      await supabase.storage.from(BUCKET).upload(path, file, { upsert: true });
    }
    await loadFiles();
    setUploading(false);
  };

  const handleDelete = async (path) => {
    await supabase.storage.from(BUCKET).remove([path]);
    await loadFiles();
  };

  return (
    <div style={{ marginBottom: 40 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ width: 4, height: 26, background: '#D60000', borderRadius: 2, flexShrink: 0, marginRight: 12 }} />
        <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 16, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#fff' }}>{category.label}</div>
        <div style={{ marginLeft: 'auto', fontSize: 11, color: '#555' }}>{loading ? 'Loading...' : `${files.length} file${files.length !== 1 ? 's' : ''}`}</div>
      </div>

      {needsNote && (
        <div style={{ background: 'rgba(214,0,0,0.08)', border: '1px solid rgba(214,0,0,0.25)', borderLeft: '3px solid #D60000', borderRadius: 3, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: '#ff7777' }}>
          Required before content creation can begin — {category.desc}
        </div>
      )}

      <div
        style={{ border: `2px dashed ${dragOver ? '#D60000' : '#2a2a2a'}`, borderRadius: 4, background: dragOver ? 'rgba(214,0,0,0.04)' : '#131313', padding: '28px 24px', textAlign: 'center', cursor: 'pointer', marginBottom: 16, transition: 'all 0.15s' }}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); handleUpload(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
      >
        <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: uploading ? '#D60000' : '#555', marginBottom: 4 }}>
          {uploading ? 'Uploading...' : 'Drop files here or click to browse'}
        </div>
        <div style={{ fontSize: 11, color: '#3a3a3a' }}>{category.desc}</div>
        <input ref={inputRef} type="file" accept={category.accept} multiple style={{ display: 'none' }} onChange={e => handleUpload(e.target.files)} />
      </div>

      {loading ? (
        <div style={{ background: '#131313', border: '1px solid #1e1e1e', borderRadius: 4, padding: 24, textAlign: 'center', fontSize: 12, color: '#333' }}>Loading...</div>
      ) : files.length === 0 ? (
        <div style={{ background: '#131313', border: '1px solid #1e1e1e', borderRadius: 4, padding: 24, textAlign: 'center', fontSize: 12, color: '#333' }}>No {category.label.toLowerCase()} uploaded yet</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
          {files.map(f => <FileCard key={f.id} file={{ ...f, name: `${category.id}/${f.name}` }} onDelete={handleDelete} />)}
        </div>
      )}
    </div>
  );
}

export default function Assets() {
  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '32px 48px 60px' }}>
      <div style={{ background: '#131313', border: '1px solid #1e1e1e', borderLeft: '3px solid #3a3a3a', borderRadius: 3, padding: '12px 16px', marginBottom: 32, fontSize: 12, color: '#555', lineHeight: 1.6 }}>
        Files upload directly to Supabase cloud storage — they save permanently and are accessible from any device.
      </div>

      {CATEGORIES.map((cat, i) => (
        <UploadSection
          key={cat.id}
          category={cat}
          needsNote={cat.id === 'video' || cat.id === 'photos'}
        />
      ))}
    </div>
  );
}
